import { defineStore } from 'pinia';
import { computed, nextTick, ref } from 'vue';
import { api, ApiError, uploadFile, verifyAnnotationFile, type TransferProgress } from './api';
import { errorText } from './locale';
import { verifySaveResult, verifyLatestSave } from './saveVerification';
import type { DentalAnnotations, DentalAsset, DentalRevision, DentalTask } from './types';

export const useDentalTaskStore = defineStore('dental-task', () => {
  const task = ref<DentalTask>(); const history = ref<DentalRevision[]>([]);
  const viewedRevisionId = ref('');
  const error = ref(''); const busy = ref(false); const leaseEditable = ref(false); const savedMessage = ref('');
  const savePhase = ref<'idle' | 'preparing' | 'uploading' | 'registering' | 'committing' | 'verifying' | 'success' | 'error'>('idle');
  const saveProgress = ref<TransferProgress | null>(null); const saveError = ref('');
  const confirmedSave = ref<{ number: number; at: string }>(); const showSaveToast = ref(false);
  const confirmationPending = ref(false);
  const editable = computed(() => leaseEditable.value && !busy.value && !confirmationPending.value);
  type Prepared = { annotations: DentalAnnotations; files?: { asset: DentalAsset; file: File }[] };
  type SaveResult = { task: DentalTask; revision: DentalRevision };
  let pendingSave: { taskId: string; payload: ReturnType<typeof makePayload>; prepared: Prepared; result?: SaveResult; onSaved: (revision: DentalRevision) => void } | undefined;
  const sessionId = crypto.randomUUID();
  let generation = 0;
  let leaseToken = ''; let timer: ReturnType<typeof setInterval> | undefined;
  const sourceVolume = computed(() => task.value?.assets.find(a => a.id === task.value?.volumeAssetId));
  const sourceMesh = computed(() => task.value?.assets.find(a => a.id === task.value?.meshAssetId));
  const sourceMri = computed(() => task.value?.assets.find(a => a.id === task.value?.mriAssetId));
  function leaseBody() { return { sessionId, leaseToken }; }
  function stopLease() { if (timer) clearInterval(timer); timer = undefined; leaseEditable.value = false; leaseToken = ''; }
  async function heartbeat() {
    if (!task.value || !leaseEditable.value) return;
    try { await api(`dental/tasks/${task.value.id}/heartbeat`, 'POST', leaseBody()); }
    catch (e) { leaseEditable.value = false; error.value = (e as Error).message; }
  }
  async function load(id: string) {
    generation++;
    stopLease();
    pendingSave = undefined; confirmationPending.value = false; savePhase.value = 'idle'; saveError.value = ''; saveProgress.value = null; confirmedSave.value = undefined; showSaveToast.value = false;
    busy.value = true; error.value = ''; savedMessage.value = ''; leaseEditable.value = false;
    try {
      task.value = await api(`dental/tasks/${id}`);
      viewedRevisionId.value = task.value!.activeRevisionId;
      history.value = await api(`dental/tasks/${id}/revisions`);
      if (!task.value?.capabilities?.edit) return;
      try {
        const checkout = await api<{ lease: { token: string }; taskVersion: number; baseRevisionId: string }>(`dental/tasks/${id}/checkout`, 'POST', { sessionId });
        leaseToken = checkout.lease.token;
        // Re-read after checkout: another editor could have saved during load.
        task.value = await api(`dental/tasks/${id}`);
        viewedRevisionId.value = task.value!.activeRevisionId;
        leaseEditable.value = true;
        if (timer) clearInterval(timer);
        timer = setInterval(heartbeat, 30000);
      } catch (e) { if (e instanceof ApiError && [403, 409].includes(e.status)) error.value = e.message; else throw e; }
    } finally { busy.value = false; }
  }
  async function workflow(action: 'assign' | 'submit' | 'review' | 'reopen', extra: Record<string, unknown> = {}) {
    if (!task.value) throw new Error('Load a task first.');
    const snapshot = task.value;
    try {
      const result = await api<DentalTask>(`dental/tasks/${snapshot.id}/${action}`, 'POST', {
        version: snapshot.version, baseRevisionId: snapshot.activeRevisionId, workflowVersion: snapshot.workflowVersion,
        ...(action === 'submit' ? leaseBody() : {}), ...extra,
      });
      stopLease(); task.value = { ...snapshot, ...result };
      task.value = await api(`dental/tasks/${snapshot.id}`);
      error.value = '';
    } catch (e) { if (e instanceof ApiError && e.status === 409) { stopLease(); error.value = e.message; } throw e; }
  }
  async function annotationAsset(id: string) { return api<DentalAsset>(`dental/assets/${id}`); }
  async function stageFile(file: File, kind: 'labelmap' | 'meshLabels', extra: Record<string, unknown>) {
    if (!task.value || !leaseEditable.value || confirmationPending.value) throw new Error('Reopen the task to acquire an editor lease.');
    const snapshot = task.value; const run = generation;
    const { assetFolderId } = await api<{ assetFolderId: string }>(`dental/cases/${snapshot.caseId}`);
    if (run !== generation) throw new Error('保存页面已切换，请在当前任务重新保存。');
    savePhase.value = 'uploading';
    const fileId = await uploadFile(assetFolderId, file, progress => { saveProgress.value = progress; });
    if (run !== generation) throw new Error('保存页面已切换，请在当前任务重新保存。');
    savePhase.value = 'registering'; saveProgress.value = null;
    return api<DentalAsset>(`dental/cases/${snapshot.caseId}/assets`, 'POST', {
      kind, fileId, clientRequestId: crypto.randomUUID(), ...extra,
      provenance: { dataKind: 'browser-annotation', sourceTaskId: snapshot.id, sourceRevisionId: snapshot.activeRevisionId },
    });
  }
  function makePayload(snapshot: DentalTask, annotations: DentalAnnotations) {
    return { ...leaseBody(), version: snapshot.version, baseRevisionId: snapshot.activeRevisionId,
      clientRequestId: crypto.randomUUID(), annotations: JSON.parse(JSON.stringify(annotations)) as DentalAnnotations };
  }
  async function finishSave() {
    const operation = pendingSave!;
    const assertCurrent = () => { if (pendingSave !== operation) throw new Error('保存页面已切换，本次结果不应用到其他任务。'); };
    savePhase.value = 'committing'; saveProgress.value = null;
    if (!operation.result) {
      // The same immutable request can safely be resent after an ambiguous
      // connection failure. Do not upload another file or create a new ID.
      for (let attempt = 0; attempt < 2; attempt++) {
        try { operation.result = await api<SaveResult>(`dental/tasks/${operation.taskId}/revisions`, 'POST', operation.payload, { signal: AbortSignal.timeout(120000) }); break; }
        catch (e) { if (attempt || (e instanceof ApiError && e.status < 500)) throw e; }
      }
    }
    const revision = operation.result!.revision;
    assertCurrent();
    verifySaveResult(operation.taskId, operation.payload.version, operation.payload.annotations, revision);
    savePhase.value = 'verifying';
    let latest = await api<DentalTask>(`dental/tasks/${operation.taskId}`, 'GET', undefined, { signal: AbortSignal.timeout(45000) });
    assertCurrent();
    verifyLatestSave(latest, revision, operation.payload.annotations);
    for (const { asset, file } of operation.prepared.files ?? []) {
      const stored = latest.assets.find(row => row.id === asset.id);
      const linked = asset.kind === 'labelmap' ? revision.annotations.labelmapAssetId : revision.annotations.meshLabelsAssetId;
      if (!stored || linked !== asset.id || stored.fileId !== asset.fileId || stored.caseId !== latest.caseId) throw new Error('服务器保存版本没有引用本次上传的标注文件。');
      await verifyAnnotationFile(stored, file, progress => { saveProgress.value = progress; });
      assertCurrent();
    }
    // Recheck the active pointer AFTER the bytes, so a superseded revision is
    // never announced as the latest saved annotation.
    latest = await api<DentalTask>(`dental/tasks/${operation.taskId}`, 'GET', undefined, { signal: AbortSignal.timeout(45000) });
    assertCurrent();
    verifyLatestSave(latest, revision, operation.payload.annotations);
    task.value = latest; viewedRevisionId.value = revision.id;
    operation.onSaved(revision);
    pendingSave = undefined; confirmationPending.value = false;
    confirmedSave.value = { number: revision.number, at: new Date().toISOString() };
    savePhase.value = 'success'; saveProgress.value = null; showSaveToast.value = true;
    savedMessage.value = `保存成功 · 第${revision.number}版 · 服务器已确认最新标注`;
    const completedGeneration = generation;
    try { const revisions = await api<DentalRevision[]>(`dental/tasks/${operation.taskId}/revisions`); if (generation === completedGeneration) history.value = revisions; }
    catch { if (generation === completedGeneration) error.value = '保存已确认，历史列表暂未刷新。'; }
    return revision;
  }
  function saveFailed(e: unknown) {
    savePhase.value = 'error'; showSaveToast.value = false; saveProgress.value = null;
    const detail = e instanceof TypeError ? '服务器连接中断，请检查网络。' : e instanceof Error && ['TimeoutError','AbortError'].includes(e.name) ? '服务器确认超时。' : errorText(e);
    saveError.value = confirmationPending.value ? `保存尚未确认，当前修改仍保留在此页面。${detail} 可重试确认。` : `保存失败，当前修改仍未保存。${detail}`;
    if (e instanceof ApiError && e.status === 409) leaseEditable.value = false;
  }
  async function save(prepare: () => Promise<Prepared>, onSaved: (revision: DentalRevision) => void) {
    if (busy.value || pendingSave) return;
    if (!task.value || !editable.value) { saveFailed(new Error('请重新打开任务获取编辑权限。')); return; }
    const snapshot = task.value;
    const run = generation;
    busy.value = true; savePhase.value = 'preparing'; saveProgress.value = null;
    saveError.value = ''; savedMessage.value = ''; error.value = ''; showSaveToast.value = false;
    try {
      await nextTick(); await new Promise(resolve => setTimeout(resolve, 0));
      const prepared = await prepare();
      if (run !== generation) return;
      pendingSave = { taskId: snapshot.id, prepared, payload: makePayload(snapshot, prepared.annotations), onSaved };
      confirmationPending.value = true;
      return await finishSave();
    } catch (e) { if (run === generation) saveFailed(e); }
    finally { if (run === generation) busy.value = false; }
  }
  async function retrySave() {
    if (busy.value || !pendingSave) return;
    const run = generation;
    busy.value = true; saveError.value = ''; error.value = '';
    try { return await finishSave(); }
    catch (e) { if (run === generation) saveFailed(e); }
    finally { if (run === generation) busy.value = false; }
  }
  function dispose() {
    generation++;
    if (timer) clearInterval(timer);
    if (task.value && leaseEditable.value) void api(`dental/tasks/${task.value.id}/release`, 'POST', leaseBody()).catch(() => {});
    pendingSave = undefined; confirmationPending.value = false;
    stopLease();
  }
  return { task, history, viewedRevisionId, error, busy, editable, savedMessage, sourceVolume, sourceMesh, sourceMri, savePhase, saveProgress, saveError, confirmedSave, showSaveToast, confirmationPending, retrySave, load, annotationAsset, stageFile, save, workflow, dispose };
});
